/**
 * Wires the local-gateway circuit breaker (./index) into the Cornerstone DICOM
 * loader. Split out of ./index so that `chooseStorageLocation` — a plain pure
 * function imported by imageIds.ts — doesn't drag `@cornerstonejs/dicom-image-loader`
 * into every consumer's dependency graph; only lib/cornerstone/init.ts (which
 * already depends on the full Cornerstone stack) imports this module.
 */
import { Enums, eventTarget, imageLoader } from '@cornerstonejs/core';
import { internal as dicomLoaderInternal } from '@cornerstonejs/dicom-image-loader';
import { isDeliberateAbort } from './abortedRequests';
import { freshestUrlFor } from './freshUrlRegistry';
import { cloudUrlForLocalUrl, isLocalEnabled, noteDispatchedOutcome, noteDispatchedSource, originOf, reprobeLocalGatewayIfDue, } from './index';
import { resetInFlightRequestsForTests, trackInFlight } from './inFlightRequests';
/**
 * Resolve the URL the DICOM loader should actually dispatch a request to.
 *
 * This is where the local-vs-cloud decision for raster images is *made*, not
 * merely corrected. imageIds embed the LAN URL whenever the study has one (see
 * imageIdSourceUrl) precisely so that this hook — which runs at request time,
 * not render time — gets to decide with the freshest possible knowledge:
 *
 *  - origin proven reachable  -> dispatch to the LAN
 *  - probe still in flight    -> dispatch to the cloud, so no image ever waits
 *                                on a LAN round-trip that may not arrive
 *  - probe failed, or breaker tripped mid-session -> dispatch to the cloud
 *                                (and, once a failed probe has expired, kick off
 *                                a fresh one for later requests to benefit from)
 *
 * Deciding here rather than in the imageId is what makes a study that renders
 * once and never again still able to use the gateway: the probe settles while
 * the first images are already in flight, and every request dispatched after
 * that point picks up the result with no re-render involved.
 *
 * URLs with no tracked local/cloud pairing (the whole app outside this feature)
 * pass through unchanged.
 *
 * Whichever URL wins is then re-signed from ./freshUrlRegistry. imageIds are
 * built once and pinned for the session (that is what makes them usable as
 * cache keys), so the signature they embed is the one the FIRST study response
 * carried — dead an hour later. The study poll keeps delivering fresh
 * signatures; this is where they are consumed, at dispatch time, without any
 * imageId — or any Cornerstone cache entry — changing.
 */
function resolveDispatchUrl(url) {
    const cloudUrl = cloudUrlForLocalUrl(url);
    const chosen = !cloudUrl ? url : isLocalEnabled(originOf(url)) ? url : cloudUrl;
    // Going to the cloud because the gateway's last probe failed: if that verdict
    // has expired, probe again in the background, so a viewer that stays open on
    // one study still moves to the LAN once the gateway can be reached.
    if (cloudUrl && chosen === cloudUrl)
        reprobeLocalGatewayIfDue(originOf(url));
    // Remember where this one went. The imageId names the LAN URL either way, and
    // the phase can change before the request settles, so the failure and success
    // handlers below have no other way to tell a LAN outcome from a cloud one.
    if (cloudUrl)
        noteDispatchedSource(url, chosen);
    return freshestUrlFor(chosen);
}
function extractFailedImageIds(evt) {
    const detail = evt.detail;
    if (!detail)
        return [];
    // StackViewport.loadImages dispatches the rejected-results array directly;
    // the imageId, when present, lives on the rejection reason.
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item.reason?.imageId)
            .filter((id) => Boolean(id));
    }
    return detail.imageId ? [detail.imageId] : [];
}
function handleLocalLoadFailure(evt) {
    // A request the app aborted itself (MPR teardown / retry) is not evidence
    // against the gateway.
    const detail = evt.detail;
    if (detail && !Array.isArray(detail) && isDeliberateAbort(detail.error))
        return;
    for (const imageId of extractFailedImageIds(evt)) {
        const url = imageId.replace(/^wadouri:/, '');
        const cloudUrl = cloudUrlForLocalUrl(url);
        if (!cloudUrl)
            continue; // not a tracked local-gateway image — nothing to do
        // Counted against the LAN only if that is where this request was sent: while
        // the probe is in flight these same imageIds are served from the cloud.
        noteDispatchedOutcome(url, false);
        // Best-effort warm the cloud object under its own imageId, so a later fresh
        // build of this same image (series re-open, MPR/fusion re-derivation) that
        // lands on the cloud URL resolves instantly instead of re-fetching.
        imageLoader.loadAndCacheImage(`wadouri:${cloudUrl}`).catch(() => { });
    }
}
function handleLocalLoadSuccess(evt) {
    const detail = evt.detail;
    const imageId = detail?.image?.imageId;
    if (!imageId)
        return;
    const url = imageId.replace(/^wadouri:/, '');
    if (!cloudUrlForLocalUrl(url))
        return; // not a tracked local-gateway image
    noteDispatchedOutcome(url, true);
}
// Cornerstone emits per-image failures as two events on the global eventTarget
// (IMAGE_LOAD_FAILED from imageLoader, IMAGE_LOAD_ERROR from StackViewport) —
// see captureViewerError.ts for the same pattern. A failing frame fires both;
// noteDispatchedOutcome/loadAndCacheImage are idempotent-enough that double-counting
// a single failure just costs one extra (harmless, deduped-by-cache) warm call.
let fallbackRegistered = false;
/**
 * Inactivity deadline for a single DICOM object fetch.
 *
 * Cornerstone's xhrRequest sets no timeout, so a socket that hangs without
 * ever erroring (Wi-Fi flip, sleeping laptop, gateway that stops answering
 * without RST) leaves the load promise pending forever — and, because a hang
 * fires neither IMAGE_LOAD_FAILED nor IMAGE_LOAD_ERROR, it can never trip the
 * LAN circuit breaker either.
 *
 * This is an IDLE watchdog, not `xhr.timeout`: that one is total wall-clock,
 * and this hook serves every wadouri request including multi-hundred-MB
 * cineloops that legitimately stream for minutes on a clinic uplink. The
 * timer re-arms on every `progress` event, so a transfer that keeps moving is
 * never cut; only bytes-stopped-arriving aborts (→ xhrRequest rejects →
 * IMAGE_LOAD_FAILED, so the caller can retry and the breaker sees it).
 */
export const IMAGE_REQUEST_IDLE_TIMEOUT_MS = 30_000;
function armIdleWatchdog(xhr) {
    let timer;
    const rearm = () => {
        if (timer !== undefined)
            clearTimeout(timer);
        timer = setTimeout(() => xhr.abort(), IMAGE_REQUEST_IDLE_TIMEOUT_MS);
    };
    xhr.addEventListener('progress', rearm);
    xhr.addEventListener('loadend', () => {
        if (timer !== undefined)
            clearTimeout(timer);
    });
    rearm();
}
/** Idempotent — wires the circuit breaker into the DICOM loader. Call once at init. */
export function registerLocalGatewayFallback() {
    if (fallbackRegistered)
        return;
    fallbackRegistered = true;
    dicomLoaderInternal.setOptions({
        open: (xhr, url) => {
            xhr.open('get', resolveDispatchUrl(url), true);
            trackInFlight(url, xhr);
            armIdleWatchdog(xhr);
        },
    });
    eventTarget.addEventListener(Enums.Events.IMAGE_LOAD_FAILED, handleLocalLoadFailure);
    eventTarget.addEventListener(Enums.Events.IMAGE_LOAD_ERROR, handleLocalLoadFailure);
    eventTarget.addEventListener(Enums.Events.IMAGE_LOADED, handleLocalLoadSuccess);
}
/** Test-only: allow a fresh registerLocalGatewayFallback() call in isolated test runs. */
export function resetLocalGatewayFallbackRegistrationForTests() {
    fallbackRegistered = false;
    resetInFlightRequestsForTests();
}
